import { signIn } from "@/lib/auth-client";
import { passwordSchema } from "@/lib/password-schema";
import { trpc } from "@/lib/trpc";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";

const searchSchema = z.object({
	token: z.string().optional(),
});

export const Route = createFileRoute("/accept-invite")({
	component: AcceptInvitePage,
	validateSearch: searchSchema,
});

function AcceptInvitePage() {
	const { token } = Route.useSearch();

	if (!token) {
		return (
			<Shell title="Accept invitation">
				<ErrorState message="This page needs a token. Open the link from your invitation email." />
			</Shell>
		);
	}

	return <AcceptInviteFlow token={token} />;
}

function AcceptInviteFlow({ token }: { token: string }) {
	const validation = trpc.users.validateInvite.useQuery({ token });

	if (validation.isPending) {
		return (
			<Shell title="Accept invitation">
				<p className="text-sm text-[#6b7280]">Checking your invitation…</p>
			</Shell>
		);
	}

	if (validation.error) {
		return (
			<Shell title="Accept invitation">
				<ErrorState message={validation.error.message} />
			</Shell>
		);
	}

	const outcome = validation.data;

	if (outcome?.status === "valid") {
		return (
			<Shell title="Set up your account">
				<p className="mb-4 text-sm text-[#6b7280]">
					You've been invited as <strong>{outcome.email}</strong>. Choose a password to finish
					setting up your account.
				</p>
				<AcceptForm token={token} email={outcome.email} />
			</Shell>
		);
	}

	if (outcome?.status === "expired") {
		return (
			<Shell title="Invitation expired">
				<ErrorState message="This invitation has expired. Ask an admin to resend it." />
			</Shell>
		);
	}

	if (outcome?.status === "revoked") {
		return (
			<Shell title="Invitation revoked">
				<ErrorState message="This invitation was revoked. Ask an admin for a new one." />
			</Shell>
		);
	}

	if (outcome?.status === "accepted") {
		return (
			<Shell title="Invitation already used">
				<p className="text-sm text-[#6b7280]">
					This invitation link was already used. If you set up your account, go ahead and sign in.
					If you didn't, ask an admin to resend a fresh invite.
				</p>
				<Link
					to="/login"
					className="mt-4 inline-flex h-9 items-center justify-center rounded bg-[#16a34a] px-4 text-sm font-semibold text-white hover:bg-[#15803d]"
				>
					Continue to sign in
				</Link>
			</Shell>
		);
	}

	return (
		<Shell title="Invalid invitation">
			<ErrorState message="We couldn't find this invitation. Check the link or ask an admin." />
		</Shell>
	);
}

function AcceptForm({ token, email }: { token: string; email: string }) {
	const [name, setName] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const acceptMutation = trpc.users.acceptInvite.useMutation();

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setError(null);

		const parsed = passwordSchema.safeParse(password);
		if (!parsed.success) {
			setError(parsed.error.issues[0]?.message ?? "Invalid password");
			return;
		}
		if (password !== confirmPassword) {
			setError("Passwords don't match");
			return;
		}
		if (name.trim().length === 0) {
			setError("Please enter your name");
			return;
		}

		setLoading(true);
		try {
			await acceptMutation.mutateAsync({ token, name: name.trim(), password });
			// Invitation consumed. Sign in so the session cookie is set, then
			// send the user into the onboarding gate (Wave 9).
			const signInResult = await signIn.email({ email, password });
			if (signInResult.error) {
				setError(
					"Your account is set up, but we couldn't sign you in automatically. Try signing in manually.",
				);
				return;
			}
			// Full page load so TanStack Router picks up the new session.
			window.location.href = "/onboarding";
		} catch (err: unknown) {
			setError(err instanceof Error ? err.message : "Something went wrong");
		} finally {
			setLoading(false);
		}
	}

	return (
		<form onSubmit={handleSubmit} className="space-y-4">
			{error && <div className="rounded bg-[#fef2f2] p-3 text-sm text-[#dc2626]">{error}</div>}

			<div className="space-y-1">
				<label htmlFor="accept-email" className="text-xs font-medium text-[#1e1e20]">
					Email
				</label>
				<input
					id="accept-email"
					type="text"
					value={email}
					disabled
					className="h-9 w-full rounded border border-[#f3f4f6] bg-[#f8f9fb] px-3 text-sm text-[#6b7280]"
				/>
			</div>

			<div className="space-y-1">
				<label htmlFor="accept-name" className="text-xs font-medium text-[#1e1e20]">
					Your name
				</label>
				<input
					id="accept-name"
					type="text"
					value={name}
					onChange={(e) => setName(e.target.value)}
					required
					className="h-9 w-full rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] placeholder:text-[#9ca3af] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
					placeholder="First Last"
				/>
			</div>

			<div className="space-y-1">
				<label htmlFor="accept-password" className="text-xs font-medium text-[#1e1e20]">
					Password
				</label>
				<input
					id="accept-password"
					type="password"
					value={password}
					onChange={(e) => setPassword(e.target.value)}
					required
					autoComplete="new-password"
					className="h-9 w-full rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] placeholder:text-[#9ca3af] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
					placeholder="At least 12 characters"
				/>
			</div>

			<div className="space-y-1">
				<label htmlFor="accept-confirm" className="text-xs font-medium text-[#1e1e20]">
					Confirm password
				</label>
				<input
					id="accept-confirm"
					type="password"
					value={confirmPassword}
					onChange={(e) => setConfirmPassword(e.target.value)}
					required
					autoComplete="new-password"
					className="h-9 w-full rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] placeholder:text-[#9ca3af] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
				/>
			</div>

			<button
				type="submit"
				disabled={loading}
				className="h-9 w-full rounded bg-[#16a34a] text-sm font-semibold text-white hover:bg-[#15803d] disabled:opacity-50"
			>
				{loading ? "Setting up…" : "Create account"}
			</button>
		</form>
	);
}

function ErrorState({ message }: { message: string }) {
	return (
		<>
			<div className="rounded bg-[#fef2f2] p-3 text-sm text-[#dc2626]">{message}</div>
			<Link
				to="/login"
				className="mt-4 inline-flex h-9 items-center justify-center rounded border border-[#d1d5db] px-4 text-sm font-medium text-[#1e1e20] hover:bg-[#f3f4f6]"
			>
				Back to sign in
			</Link>
		</>
	);
}

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
	return (
		<div className="flex min-h-screen items-center justify-center bg-[#f8f9fb]">
			<div className="w-full max-w-sm space-y-6 rounded-lg border border-[#e5e7eb] bg-white p-8 shadow-sm">
				<div className="text-center">
					<div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-[#16a34a]">
						<span className="text-lg font-bold text-white">P</span>
					</div>
					<h1 className="text-xl font-semibold text-[#1e1e20]">{title}</h1>
				</div>
				{children}
			</div>
		</div>
	);
}
